import { DateTime } from 'luxon'
import { round } from 'lodash'

/**
 * Formats a numeric value as currency
 * @param value the number to format
 * @param precision number of decimal places (default: 2)
 * @param showSymbol whether to show the currency symbol (default: true)
 * @returns the formatted currency string
 */
export const formatCurrency = (value: number | null | undefined, precision = 2, showSymbol = true): string => {
  if (value == null) return '-'
  return value
    .toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
    })
    .replace('$', showSymbol ? '$' : '')
}

/** formatCurrency type */
export type CurrencyFormatter = typeof formatCurrency

export const tableColumnCurrency = (row: any, column: any, cellValue: any) => {
  return formatCurrency(parseFloat(cellValue))
}

export const tableColumnAdderCurrency = (row: any, column: any, cellValue: any) => {
  const value = parseFloat(cellValue)
  return `${value >= 0 ? '+' : ''}${formatCurrency(value)}`
}

export const tableColumnPercentageWithTwoDecimals = (row: any, column: any, cellValue: any) => {
  return formatPercentage(parseFloat(cellValue), 2)
}

/**
 * Formats a value as megawatts with custom decimal places
 * @param value the number to format
 * @param digits number of decimal places
 */
export const formatMegawattsCustom = (value: number | null | undefined, digits: number): string => {
  if (value == null) return '-'
  return value.toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })
}

/**
 * Formats a value as megawatts with 3 decimal places
 * @param value the number to format
 */
export const formatMegawatts = (value: number | null | undefined): string => {
  return formatMegawattsCustom(value, 3)
}

/** formatMegawatts type */
export type MegawattsFormatter = typeof formatMegawatts

const HASHRATE_H_PER_TH = 1e12

/**
 * Converts a hashrate from H/s (hashes per second) to TH/s and formats it with 2 decimal places.
 * @param value hashrate in H/s, or null/undefined for missing data
 * @returns formatted TH/s string or '—' when value is null/undefined
 */
export const formatHashrateToTerahashValue = (value: number | null | undefined): string => {
  if (value == null) return '—'
  const ths = Number(value) / HASHRATE_H_PER_TH
  return ths.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/**
 * Formats a number as a percentage
 * @param value the number to format
 * @param decimals number of decimal places (default: 1)
 */
export const formatPercentage = (value: number | null | undefined, decimals = 1): string => {
  if (value == null) return '-'
  const roundedValue = round(value, decimals)
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    style: 'percent',
  }).format(roundedValue / 100)
}

/** formatPercentage type */
export type PercentageFormatter = typeof formatPercentage

export const formatDateUSCentral = new Intl.DateTimeFormat('en-US', {
  timeZone: 'US/Central',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false, // Use 24-hour format
})

export const formatDateUSCentralShort = new Intl.DateTimeFormat('en-US', {
  timeZone: 'US/Central',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

export const formatISODateToDateString = (isoDateString: string, format = 'yyyy-MM-dd') => {
  return DateTime.fromISO(isoDateString).toFormat(format)
}

export const formatJSDateToDateString = (date: Date) => {
  return DateTime.fromJSDate(date).toFormat('yyyy-MM-dd')
}

export const formatJSDateToMonthString = (date: Date) => {
  return DateTime.fromJSDate(date).toFormat('MMMM yyyy')
}

/**
 * Formats a value as dollars per megawatt hour
 * @param value the value to format
 */
export const formatDollarPerMWh = (value: number | null | undefined): string => {
  if (value == null) return '-'
  return `${formatCurrency(value)}/MWh`
}

/**
 * Formats a value as dollars per kilowatt hour
 * @param value the value to format
 */
export const formatDollarPerKWh = (value: number | null | undefined): string => {
  if (value == null) return '-'
  return `${formatCurrency(value, 4)}/KWh`
}

/**
 * Formats a value as megawatt hours
 * @param value the value to format
 */
export const formatMWh = (value: number | null | undefined): string => {
  if (value == null) return '-'
  return `${value.toFixed(3)} MWh`
}

/**
 * Formats a temperature in Fahrenheit
 * @param value the temperature value
 */
export const formatTemperature = (value: number | null | undefined): string => {
  if (value == null) return '-'
  return `${value.toFixed(0)}°F`
}

/**
 * Formats a time range (HH:mm - HH:mm)
 * @param value the time range string in format "HH:mm-HH:mm"
 */
export const formatTimeRange = (value: string | null | undefined): string => {
  if (!value) return '-'

  const [start, end] = value.split('-')
  if (!start || !end) return value

  const formatTime = (time: string) => {
    const [hours, minutes] = time.trim().split(':')
    const date = new Date()
    date.setHours(parseInt(hours), parseInt(minutes))
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  }

  return `${formatTime(start)} - ${formatTime(end)}`
}
